import { Client } from './Client';
import { Currency } from './Currency';
import { Society } from './Society';
import { TransportationQuoteDetail } from './TransportationQuoteDetail';
import { Unit } from './Unit';
import { WarehouseQuoteDetail } from './WareHouseQuoteDetail';

// ─── Sector ──────────────────────────────────────────────────────────────────

export type OrderSector = 'TRANSPORTE' | 'ALMACENAMIENTO';

export const ORDER_SECTORS: { id: OrderSector; name: string }[] = [
    { id: 'TRANSPORTE', name: 'Transporte' },
    { id: 'ALMACENAMIENTO', name: 'Almacenamiento' },
];

// ─── Condición de pago (cantidad de pagos) ────────────────────────────────────

export const ORDER_PAYMENTS_COUNT_OPTIONS: { id: number; name: string }[] = [
    { id: 1, name: '1 pago' },
    { id: 2, name: '2 pagos' },
];

// ─── Status ───────────────────────────────────────────────────────────────────

export interface OrderStatus {
    id: string;
    name: string;
}

// ─── Order (cabecera unificada) ───────────────────────────────────────────────

export interface Order {
    id?: number;
    sector?: OrderSector;
    contact_email?: string;
    status?: string;
    status_selected?: OrderStatus;
    status_choices?: OrderStatus[];
    order?: number;
    order_type?: string;

    client_id?: number;
    client?: Client;
    client_name?: string;
    client_fantasy_name?: string;

    society_id?: number;
    society?: Society;
    society_name?: string;
    society_fantasy_name?: string;

    // FK de cotización — solo uno activo según sector
    transportation_quote_detail_id?: number;
    transportation_quote_detail?: TransportationQuoteDetail;
    warehouse_quote_detail_id?: number;
    warehouse_quote_detail?: WarehouseQuoteDetail;

    // Campos específicos de transporte
    rate_runner?: number;
    currency_rate_runner?: Currency;
    currency_rate_runner_id?: number;
    quantity_unit_rate_runner?: Unit;
    quantity_unit_rate_runner_id?: number;
    rate_transporter?: number;
    currency_rate_transporter?: Currency;
    currency_rate_transporter_id?: number;
    quantity_unit_rate_transporter?: Unit;
    quantity_unit_rate_transporter_id?: number;
    use_standard_weight?: boolean;
    standard_weight_value?: number | string | null;
    standard_weight_unit_name?: string | null;
    standard_weight_unit_id?: number | string | null;
    standard_weight_lower_limit?: number | string | null;
    standard_weight_lower_limit_unit_id?: number | string | null;

    total_guides?: number;
    created_user?: string;
    last_modified_user?: string;
    created_at?: string;
    updated_at?: string;

    // IDs legacy para backward compat con ítems (Fase 4)
    original_transportation_order_id?: number;
    original_warehouse_order_id?: number;
}

// ─── Formulario de creación / edición ────────────────────────────────────────

export interface OrderDataForm {
    id?: number;
    sector: OrderSector | string;
    sector_id?: number;
    contact_email: string;
    client_id: number | string;
    society_id: number | string;
    status?: string;
    status_selected?: OrderStatus;
    status_choices?: OrderStatus[];

    // FK de cotización (mutuamente excluyentes)
    transportation_quote_detail_id?: number | string;
    transportation_quote_detail?: TransportationQuoteDetail;
    warehouse_quote_detail_id?: number | string;
    warehouse_quote_detail?: WarehouseQuoteDetail;

    // Campos específicos de transporte
    use_standard_weight?: boolean;
    standard_weight_value?: number | string | null;
    standard_weight_unit_id?: number | string | null;
    standard_weight_unit_name?: string | null;
    standard_weight_lower_limit?: number | string | null;
    standard_weight_lower_limit_unit_id?: number | string | null;

    society_name?: string;
    client_name?: string;
    created_at?: string;
    created_user?: string;
    updated_at?: string;
    last_modified_user?: string;
}

// ─── Filtros del listado unificado ───────────────────────────────────────────

export interface OrderFilters {
    page?: number;
    per_page?: number;
    sort?: string;
    order?: 'asc' | 'desc';
    start_date?: string;
    end_date?: string;
    client?: string;
    quote_id?: number;
    orderType?: 'TODOS' | 'Transporte' | 'Almacenamiento';
    order_status?: string | null;
    order_id?: number;
    // Legacy (mantenidos para compatibilidad con AllOrdersAPIView actual)
    transportation_order_status?: string | null;
    transportation_order_id?: number;
    warehouse_order_status?: string | null;
    warehouse_order_id?: number;
}

// ─── Defaults ────────────────────────────────────────────────────────────────

export const defaultOrderDataForm: OrderDataForm = {
    sector: 'TRANSPORTE',
    contact_email: '',
    client_id: 0,
    society_id: 0,
    status: '',
    use_standard_weight: false,
    standard_weight_value: null,
    standard_weight_unit_id: null,
    standard_weight_unit_name: null,
    standard_weight_lower_limit: null,
    standard_weight_lower_limit_unit_id: null,
};

export const defaultOrderFilters: OrderFilters = {
    page: 1,
    per_page: 10,
    sort: 'id',
    order: 'desc',
    start_date: '',
    end_date: '',
    client: '',
    quote_id: undefined,
    orderType: 'TODOS',
    order_status: null,
    order_id: undefined,
    transportation_order_status: null,
    transportation_order_id: undefined,
    warehouse_order_status: null,
    warehouse_order_id: undefined,
};
